import { askAnswersMatchQuestions } from "@repo/contract/agent";
import { CHAT_CONVERSATION_PARAM, CHAT_HISTORY_ROUTE, CHAT_TICKET_PARAM, chatHistoryOutput, } from "@repo/contract/iam";
import { readJson } from "../api/auth-api";
import { assessAnswer, joinAnswerSegments } from "./answer-integrity";
import { displaySegments, parseAsk, stripAskBlock } from "./ask";
/**
 * The conversation, read back from the server (`T-58`).
 *
 * ── WHY THE APP CAN STOP BEING THE SYSTEM OF RECORD ─────────────────────────
 * The transcript was in-memory only, on an object that dies with the screen, so
 * every dock tap emptied a conversation the server still had in full. It has
 * always had it: the agent is a Durable Object with SQLite and the SDK persists
 * each finished answer whether or not anyone is connected. This is the door.
 *
 * ── THE TEXT ARRIVES RAW, AND THAT IS THE POINT ─────────────────────────────
 * Answers come back with their end-of-answer marker and their `ask` block still
 * in them. The server does not strip either, so this file runs the SAME parsers
 * a live turn runs — `assessAnswer`, `parseAsk`, `stripAskBlock` — and there is
 * one implementation of `D77` and `D112` rather than two that drift.
 */
/** The ticket is single use (`D34`), so a history read buys its own. */
export function chatHistoryUrl(base, ticket, conversationId) {
    const url = new URL(CHAT_HISTORY_ROUTE, base);
    url.searchParams.set(CHAT_TICKET_PARAM, ticket);
    url.searchParams.set(CHAT_CONVERSATION_PARAM, conversationId);
    return url.toString();
}
/**
 * One stored message as the screen renders it.
 *
 * An assistant answer whose marker never arrived is `truncated` rather than
 * dropped: unlike the live path, there is nothing left to wait for and no
 * question to re-offer — the turn is over and this is what the server kept. It
 * is marked so the bubble can say so, which is the honest half of `D77` when
 * the discard is no longer available.
 */
export function toChatMessage(stored) {
    if (stored.role === "user") {
        return {
            ask: null,
            // The app's own opening line, restored still flagged, so the screen's
            // existing filter hides it exactly as it did when the message was live.
            ...(stored.hidden === true ? { hidden: true } : {}),
            id: stored.id,
            restored: true,
            role: "user",
            state: "complete",
            text: stored.text,
        };
    }
    const verdict = assessAnswer(stored.text);
    const asks = restoredAsks(stored.asks);
    const segments = verdict.complete ? displaySegments(stored.text) : undefined;
    // The same two strips, in the same order, as the live path's `settleDraft`.
    return {
        ask: verdict.complete ? parseAsk(stored.text) : null,
        ...(asks === undefined ? {} : { asks }),
        id: stored.id,
        restored: true,
        role: "assistant",
        state: verdict.complete ? "complete" : "truncated",
        // Seams between a turn and its continuations become paragraph breaks
        // (`D191`); the final marker goes as before.
        text: stripAskBlock(joinAnswerSegments(stored.text)).trim(),
        ...(segments === undefined ? {} : { segments }),
        // The stored text, untouched, so the next turn sends this message back
        // exactly as the server has it (`T-118`). A restored message carries the
        // SERVER's id, so it is the exact-id-match case: without this the replay
        // would overwrite the stored row with the stripped copy and every
        // subsequent hydrate would read the answer as truncated.
        ...(verdict.complete ? { wire: stored.text } : {}),
    };
}
/**
 * The stored `askUser` calls, as widgets (`D122`).
 *
 * ── A RESTORED WIDGET IS LIVE, WHERE A RESTORED `ask` IS INERT ──────────────
 * `D112`'s envelope `ask` comes back dead on purpose: whether it was answered
 * was per-mount state the server never had, so re-arming it re-asked a question
 * the person may have answered yesterday. A tool call has no such gap. The
 * server holds the call AND its output, so "answered" and "still waiting" are
 * both facts it can state — an ask with an `output` restores answered, one
 * without restores armed, and no Async Storage is involved on either side.
 * That is the whole reason a widget now survives a reinstall and appears on a
 * second device.
 *
 * ── AND A MISMATCHED PAIR IS DROPPED, NOT RE-ARMED ─────────────────────────
 * `askAnswersMatchQuestions` is the one rule the schema cannot carry, and the
 * failure it catches is an answer list that does not line up with the question
 * list. The tempting repair — keep the input, drop the output — restores the
 * widget ARMED, which re-asks a question the server says was answered: exactly
 * the mistake the paragraph above exists to prevent. So the widget goes, and
 * the conversation reads as one where nothing was asked.
 */
function restoredAsks(stored) {
    if (stored === undefined) {
        return;
    }
    const widgets = stored.filter((ask) => ask.output === undefined ||
        askAnswersMatchQuestions(ask.input, ask.output));
    // Absent rather than empty: a message that asked nothing and a message whose
    // only widget was refused must look the same to the screen, because they are
    // the same — there is no control to render either way.
    return widgets.length === 0 ? undefined : widgets;
}
export function toTranscript(stored) {
    return stored.map(toChatMessage);
}
/**
 * Fetches and maps, or throws — the caller decides what an empty screen means.
 *
 * `fetch` rather than `ApiClient.authorizedFetch`: this route is on the AGENT
 * Worker and authenticates with a ticket, not with the access token. The ticket
 * is bought through the client, which is where the credential handling stays.
 */
export async function fetchChatHistory(url, fetcher = fetch) {
    const response = await fetcher(url);
    const body = await readJson(response, chatHistoryOutput);
    // `inFlight` passed through as the server sent it — absent stays absent,
    // which the socket reads as "unknown" rather than "nothing running" (`D192`).
    return {
        ...(body.inFlight === undefined ? {} : { inFlight: body.inFlight }),
        messages: toTranscript(body.messages),
    };
}
//# sourceMappingURL=history.js.map